// The help library's content. Each article is plain data so adding a tutorial
// is a matter of adding an entry here.
//
//   id           URL slug: /help/<id>
//   title        shown in the library, search, and the article header
//   description  one-line summary for cards and search results
//   category     groups cards in the library
//   keywords     extra search terms people might type
//   video.key    blob name inside the help video container (see server/routes/help.js).
//                Leave it out, or upload nothing under that name, and the article
//                shows a "video coming soon" placeholder instead.
//   action       optional { label, route } button that jumps to the feature
//   sections     ordered blocks: { step?, heading, body?, steps?, tip? }
//                step is a number shown at the right of the section's title row

const helpArticles = [
  {
    id: 'create-new-project',
    title: 'Create a new project',
    description: 'Set up a project with its details, task orders, people, and billing rates.',
    category: 'Projects',
    iconClass: 'bi bi-folder-plus',
    keywords: ['new project', 'add project', 'project wizard', 'setup', 'task order', 'rates', 'assign people', 'logo'],
    video: {
      key: 'create-new-project.mp4',
    },
    action: { label: 'Start a new project', route: '/projects/new' },
    sections: [
      {
        heading: 'Open the wizard',
        steps: [
          'Go to Projects in the sidebar.',
          'Click New Project at the top right.',
        ],
      },
      {
        step: 1,
        heading: 'Details',
        steps: [
          'Enter a Project name.',
          'Choose the PIC and enter the Contract number, if you have them.',
          'Set the Start date and End date. The end date cannot be before the start date.',
          'Under Pursuit, choose whether this is a pursuit or a regular project.',
          'Optionally, set a Location and add a Logo (PNG, JPG, or WebP up to 5 MB).',
          'Click Create & Continue.',
        ],
        tip: 'Only the name and the start and end dates are required. You can fill in the rest later from the project page.',
      },
      {
        step: 2,
        heading: 'Task orders',
        body:
          'Task orders split a project into phases. Each one has its own budget, forecast, and disciplines. '
          + 'If the project is a single phase, skip this step and the project will use one default task order.',
        steps: [
          'Click Add new to create a task order.',
          'Give it a name, start date, end date, and budget, then save it.',
          'Repeat for each phase. The project budget is the total of all task orders.',
          'Click Save & Continue.',
        ],
        tip: 'You can add more task orders at any time after the project is created.',
      },
      {
        step: 3,
        heading: 'People',
        steps: [
          'The PIC is already on the list. They are always part of the project, so they cannot be removed.',
          'Use Add project members to search for each person who will work on the project.',
          'The list shows how busy each person already is over the project dates, to help you plan staffing.',
          'Click Save & Continue once at least one person is added.',
        ],
      },
      {
        step: 4,
        heading: 'Rates',
        steps: [
          'Enter an hourly rate for each person.',
          'Check the Effective date. It defaults to the project start date.',
          'Click Finish. You are taken to the new project page.',
        ],
      },
    ],
  },
  {
    id: 'pursuits',
    title: 'Work with pursuits',
    description: 'Pull pursuits in from PursuitConnect, plan them like projects, and show or hide them.',
    category: 'Projects',
    iconClass: 'bi bi-cloud-arrow-down',
    keywords: [
      'pursuit', 'pursuits', 'pull pursuits', 'import pursuit', 'pursuitconnect', 'bid', 'proposal', 'opportunity',
      'hide pursuits', 'show pursuits', 'win likelihood', 'suggested pic', 'include pursuits',
    ],
    video: {
      key: 'pursuits.mp4',
    },
    sections: [
      {
        heading: 'What a pursuit is',
        body:
          'A pursuit is work you are bidding on but have not won yet. In ProPlan it is a project marked as a pursuit, so '
          + 'you can set up task orders, people, and forecasts for it the same way. Pursuits carry a Pursuit tag on the '
          + 'Projects page and are left out of chart totals unless you choose to include them.',
      },
      {
        step: 1,
        heading: 'Pull a pursuit from PursuitConnect',
        steps: [
          'On the Projects page, click Pull Pursuits.',
          'Search by pursuit name or owner. Each row shows the estimated start date, budget, owner, and win likelihood.',
          'Check the Suggested PIC. It is the ProPlan user whose name matches the pursuit owner.',
          'Click Create Project. The pursuit is added with its name, start date, budget, and suggested PIC.',
          'Open the new pursuit from the Projects page to add its end date, task orders, people, and rates.',
        ],
        tip: 'Already Exists means a project with the same name is already in ProPlan. Click Refresh to reload the list from PursuitConnect.',
      },
      {
        step: 2,
        heading: 'Create a pursuit by hand',
        body:
          'If the pursuit is not in PursuitConnect, click New Project and set Pursuit to Pursuit in the Details step. '
          + 'The rest of the wizard is the same as for any project.',
      },
      {
        step: 3,
        heading: 'Show or hide pursuits',
        steps: [
          'On the Projects page, click Hide Pursuits to show only regular projects.',
          'Click Show Pursuits to bring them back. Pursuits are shown again each time you open the page.',
        ],
      },
      {
        step: 4,
        heading: 'Pursuits in charts',
        steps: [
          'With All projects selected on the Charts page, pursuits are left out by default.',
          'Tick Include pursuits under Scope to add them, for example to see total planned work including bids.',
          'Picking a single pursuit in the Project filter always shows its charts.',
        ],
      },
      {
        heading: 'If Pull Pursuits is missing',
        body:
          'The button only appears when PursuitConnect is connected. You can check this in Configuration > Integrations '
          + 'under Pursuit source.',
      },
    ],
  },
  {
    id: 'set-up-discipline',
    title: 'Set up a discipline',
    description: 'Add a discipline to a task order, fill it with activities, and assign a lead and supports.',
    category: 'Projects',
    iconClass: 'bi bi-diagram-3',
    keywords: [
      'discipline', 'new discipline', 'add discipline', 'task', 'activity', 'activities', 'wbs', 'template',
      'lead', 'supports', 'assign people', 'team', 'labor', 'odc',
    ],
    video: {
      key: 'set-up-discipline.mp4',
    },
    sections: [
      {
        heading: 'Before you start',
        body:
          'Only project members can lead or support work in a discipline. If someone is missing, open the project, '
          + 'click + next to People, choose the person, and click Add.',
      },
      {
        step: 1,
        heading: 'Create the discipline',
        steps: [
          'Open the project, then click View Task Order on the task order the work belongs to.',
          'Under Disciplines, click Add Discipline.',
          'Enter the Discipline name and, optionally, a Budget.',
          'Choose the Discipline type: Labor for work people log hours against, or ODC for other direct costs.',
          'Choose the Discipline lead, then click Create.',
        ],
        tip: 'To start from a standard set of activities, click Add from WBS instead, pick a template, and untick any activities you do not need.',
      },
      {
        step: 2,
        heading: 'Add activities',
        steps: [
          'Click the new discipline in the Disciplines list to open it.',
          'Click Add Activity, enter a name, and optionally add Assumptions.',
          'Choose a Lead and any Supports for the activity, then click Create.',
          'Repeat for each activity. If the discipline is empty, Import from WBS adds activities from a template.',
        ],
      },
      {
        step: 3,
        heading: 'Assign people',
        steps: [
          'In the activities table, click the pencil in the Lead column to set or change who leads an activity.',
          'Click the edit button in the Supports column to add or remove the people supporting it. The lead is left out of this list.',
        ],
        tip: 'The lead and supports are the people who get a row when you forecast hours for that activity. ODC disciplines track costs rather than hours, so their items have no lead or supports.',
      },
    ],
  },
  {
    id: 'forecast-project-hours',
    title: 'Forecast hours',
    description: 'Plan the hours each person will spend on an activity, month by month.',
    category: 'Projects',
    iconClass: 'bi bi-clock',
    keywords: [
      'forecast', 'forecasting', 'hours', 'plan hours', 'projection', 'staffing', 'utilization', 'discipline',
      'activity', 'lead', 'supports', 'shift hours', 'export', 'past months', 'locked',
    ],
    video: {
      key: 'forecast-project-hours.mp4',
    },
    sections: [
      {
        heading: 'Open the forecast',
        body:
          'Hours are forecast per activity. Activities belong to a discipline, and disciplines belong to a task order.',
        steps: [
          'Open the project from Projects, then click View Task Order on the task order you want.',
          'Under Disciplines, click the discipline to open its details.',
          'Click Forecast Hours to start with the first activity, or click any activity row to forecast that one.',
        ],
        tip: 'Only the activity\'s lead and supports get a row on its forecast. Set them in the Lead and Supports columns on the discipline page, or use Edit supports on the forecast page.',
      },
      {
        step: 1,
        heading: 'Pick an activity',
        steps: [
          'Use the activity picker at the top to switch between activities in the discipline.',
          'Click View All to see every activity in the discipline at once, and View Single to go back.',
          'Use the arrows beside the month headers to move to earlier or later months.',
        ],
      },
      {
        step: 2,
        heading: 'Enter hours',
        steps: [
          'Each row is a person and each column is a month. Type the hours for that person in each month.',
          'Check the util figure under each cell. It shows how much of the person\'s available time is already committed that month, across all of their projects.',
          'The Total row and Total (h) column add up as you type.',
        ],
        tip: 'Available time is the month\'s working hours, minus company close dates and the person\'s time off, and reduced by any reserved hours.',
      },
      {
        step: 3,
        heading: 'Save',
        steps: [
          'Click Save at the top of the page. Nothing is stored until you do.',
          'If you leave the page or switch activities with unsaved changes, you are asked whether to discard them.',
        ],
      },
      {
        heading: 'Past months',
        body:
          'Months that have already ended are locked so past forecasts are not changed by accident. If you do need to '
          + 'correct one, contact Kelton.',
      },
      {
        heading: 'More tools',
        steps: [
          'Filter for months with hours hides empty months so long projects are easier to read.',
          'Export Excel downloads the forecast for a range of months.',
          'On the discipline page, Shift Hours moves every forecast in the discipline earlier or later by a number of months, which helps when a schedule slips.',
        ],
      },
    ],
  },
  {
    id: 'actuals',
    title: 'Actuals',
    description: 'Compare actual hours with the forecast, pull hours in, export them, and get them by email.',
    category: 'Projects',
    iconClass: 'bi bi-clipboard-data',
    keywords: [
      'actuals', 'actual hours', 'timesheets', 'timecamp', 'pull hours', 'refresh hours', 'projected', 'variance',
      'over budget', 'email alerts', 'email', 'report', 'schedule', 'export', 'excel', 'xlsx', 'print',
      'task linking', 'unmatched users', 'manual hours',
    ],
    video: {
      key: 'actuals.mp4',
    },
    sections: [
      {
        heading: 'Open actuals',
        steps: [
          'On a task order, click Actuals to see every discipline in that task order.',
          'On a discipline, click Actuals to see just that discipline.',
          'From a project page, click Actuals and choose a task order.',
        ],
      },
      {
        step: 1,
        heading: 'Read the table',
        steps: [
          'Each row is a person. Each month has two columns: Proj, the forecast hours, and Act, the actual hours.',
          'The badge shows the difference. Green is within 5% of the forecast, yellow is further off, and red means more than 10% over the forecast, or hours logged with no forecast at all.',
          'A ! on a badge flags hours well over the forecast, or a person who is unlikely to reach their forecast with the time left in the month. Hover it for the reason.',
          'Discipline breakdown below shows the same numbers per discipline.',
          'Use the arrows to move between months.',
        ],
      },
      {
        step: 2,
        heading: 'Pull hours in',
        body:
          'When hours come from a time tracker, each month has a Pull hours button, or Refresh hours once it has been '
          + 'pulled. Refreshed shows how long ago the last pull was. Future months cannot be pulled.',
        steps: [
          'Click Pull hours or Refresh hours under the month.',
          'If some disciplines are not linked to the time tracker, Task linking status opens. Each discipline shows Linked, Missing, or Ignored.',
          'Click Link task to open that discipline\'s actuals and link it, or Ignore for disciplines that never log time. Pull anyway pulls what is linked.',
          'If hours come back for people ProPlan does not recognize, they are listed with the tasks affected. Make sure each one is set up in User Management, then pull again.',
        ],
        tip: 'If a warning says the source for actual hours is not configured, click Click here to configure and set it up in Configuration > Integrations.',
      },
      {
        heading: 'Entering hours by hand',
        body:
          'When there is no time tracker, open a discipline\'s actuals and click a person\'s cell to type in their actual hours.',
      },
      {
        step: 3,
        heading: 'Export and print',
        steps: [
          'Click Export, choose the From month and To month, then click Export.',
          'You get an Excel file with two sheets: Rollup, by person, and Discipline breakdown.',
          'Click Print for a printer-friendly version of the page.',
        ],
      },
      {
        step: 4,
        heading: 'Set up email alerts',
        body:
          'An email alert sends you the actuals report on a schedule, with the Excel file attached. When hours come from a '
          + 'time tracker, they are pulled again just before each email, so it is never out of date.',
        steps: [
          'Click Email Alerts.',
          'Choose the Frequency: Daily, Weekly, or Monthly.',
          'Pick the Time of day, plus the Day of week or Day of month.',
          'Under Data to include, choose how many months back the report covers.',
          'Click Add alert.',
        ],
        tip: 'Alerts go to the email on your account, at times in your own timezone. An alert set up on a task order covers every discipline in it; one set up on a discipline covers only that discipline.',
      },
      {
        heading: 'Manage your alerts',
        steps: [
          'Your alerts lists every alert you have set up here, with its schedule.',
          'Use the switch to pause or resume an alert, and the delete button to remove it.',
        ],
      },
      {
        heading: 'Check pace',
        body:
          'Click View Pace to see who is on track to log their forecast this month, who is running under or over, '
          + 'and how many hours a day each person needs. See the Check pace tutorial.',
      },
    ],
  },
  {
    id: 'pace',
    title: 'Check pace',
    description: 'See who is on track to log their forecast this month, who is running under or over, and why.',
    category: 'Projects',
    iconClass: 'bi bi-speedometer2',
    keywords: [
      'pace', 'on pace', 'behind', 'under pace', 'over pace', 'ahead', 'burn rate', 'month progress', 'utilization',
      'can\'t reach', 'over forecast', 'likely finish', 'projection', 'hours per day', 'tolerance', 'pace settings',
    ],
    video: {
      key: 'pace.mp4',
    },
    sections: [
      {
        heading: 'Open pace',
        steps: [
          'Open Actuals for a task order or a discipline.',
          'Click View Pace, next to Email Alerts, Export, and Print.',
          'Pace opens on the current month. Use the arrows to move to another month, and This month to come back.',
        ],
        tip: 'Pace on a task order covers every discipline in it. Pace on a discipline covers only that discipline.',
      },
      {
        heading: 'How pace is measured',
        body:
          'Pace compares the share of a person\'s forecast they have logged with the share of their month that has passed. '
          + 'Their month is their own working time: weekdays, less company close dates, less their time off, scaled by their FTE. '
          + 'Someone who was on vacation for the first week is not marked under pace for that week. '
          + 'Actuals are counted through yesterday, since hours are pulled overnight.',
      },
      {
        step: 1,
        heading: 'Read the summary',
        steps: [
          'The rings compare the team as a whole. The outer grey ring is how far through the month the team is; the inner ring is how much of the forecast is logged. When the inner ring trails the outer one, the team is under pace.',
          'Team pace is the same comparison as a percentage. 100% means hours are being logged exactly as fast as planned.',
          'Likely finish is the range of hours the team is heading for by the end of the month, next to the total forecast.',
        ],
      },
      {
        step: 2,
        heading: 'Read each person',
        steps: [
          'The solid bar is the hours logged so far.',
          'The dark tick is where they should be today if they log evenly through the month.',
          'The dashed line is their forecast (100%).',
          'The shaded band is where they are likely to finish: between carrying on at their current rate and switching to the planned rate for the rest of the month, capped by the time they have left.',
          'Needs is how many hours a day they must log from now on to land on their forecast. It turns red above 8 hours a day.',
          'Hover a row for the full detail: expected hours by now, time off, and how much time they have left this month across all projects.',
        ],
      },
      {
        step: 3,
        heading: 'Understand the statuses',
        steps: [
          'Can\'t reach: the hours left to log are more than the time they have left this month.',
          'Over forecast: they have already logged more than their forecast.',
          'Under pace and Over pace: they are outside the on-pace band, in either direction. Running over is as much a problem as running under, because it burns hours the forecast did not plan for.',
          'On pace: within the band.',
          'Unplanned: hours logged with no forecast this month.',
          'Too early: no working days have passed yet.',
          'For a month that is over, the statuses become Met forecast, Fell short, or Over forecast.',
        ],
        tip: 'Click a status at the top to show only those people, and use the sort menu to order by pace, hours left to log, or name. Needs attention first puts the people furthest off pace at the top.',
      },
      {
        step: 4,
        heading: 'Change what on pace means',
        body:
          'The on-pace band is set per project and defaults to 10% either side. Teams that log hours in bursts may want a wider band.',
        steps: [
          'Click the gear button at the top right, which shows the current band, for example On pace \u00b110%.',
          'Enter how far below and how far above where they should be today still counts as on pace. The preview shows the resulting bands.',
          'Click Save for project. Everyone who opens pace on this project sees the new band.',
          'Click Reset to default to go back to \u00b110%.',
        ],
      },
    ],
  },
  {
    id: 'link-timecamp-task',
    title: 'Link a TimeCamp task',
    description: 'Connect a discipline to the TimeCamp task its people log time against, so its hours can be pulled in.',
    category: 'Projects',
    iconClass: 'bi bi-link-45deg',
    keywords: [
      'timecamp', 'link task', 'task linking', 'link timecamp', 'map task', 'mapping', 'select task', 'time tracker',
      'pull hours', 'not linked', 'missing', 'ignore', 'ignored', 'stop ignoring', 'change link',
    ],
    video: {
      key: 'link-timecamp-task.mp4',
    },
    sections: [
      {
        heading: 'Why link a task',
        body:
          'When actual hours come from TimeCamp, ProPlan needs to know which TimeCamp task each discipline\'s hours are '
          + 'logged under. Linking a discipline to its TimeCamp task sets this up, so Pull hours brings in the right '
          + 'hours for it.',
      },
      {
        step: 1,
        heading: 'Open the discipline\'s actuals',
        steps: [
          'Open the project, click View Task Order, then click the discipline.',
          'Click Actuals.',
          'If the discipline is not linked yet, a yellow banner at the top says No TimeCamp task linked for this task.',
        ],
        tip: 'When you pull hours on a task order, Task linking status lists any disciplines that are not linked. Click Link task next to one to go straight to its actuals.',
      },
      {
        step: 2,
        heading: 'Choose the TimeCamp task',
        steps: [
          'Click Select Task on the banner. The Link TimeCamp task window opens and loads your TimeCamp tasks.',
          'Click Select the TimeCamp task… and work down through the TimeCamp projects and tasks, or type to search. The number after each name is its TimeCamp ID.',
          'Check Selected project/task, which shows the name, ID, and full path of what you picked.',
          'Click Save. Saved to task. appears when the link is stored. Click Close.',
        ],
        tip: 'If a task you just created in TimeCamp is not in the list, click the refresh button at the top right of the window to reload it.',
      },
      {
        step: 3,
        heading: 'Check the link and pull hours',
        steps: [
          'The banner turns green and says TimeCamp task successfully linked, with the task name.',
          'Click Pull hours under a month to bring in the hours logged against that task.',
        ],
      },
      {
        heading: 'Change a link',
        body:
          'Click Edit on the green banner, choose a different TimeCamp task, and click Save. Hours pulled from then on '
          + 'come from the new task.',
      },
      {
        heading: 'Disciplines that never log time',
        steps: [
          'Some disciplines, such as ODC disciplines, do not need a TimeCamp task. Linking is optional for ODC.',
          'In Task linking status, click Ignore next to the discipline so it is no longer flagged when you pull hours.',
          'To undo this, open the discipline\'s actuals and click Stop ignoring. Linking a task also clears it.',
        ],
      },
      {
        heading: 'If Select Task is missing',
        body:
          'The TimeCamp banner only appears when TimeCamp is the source for actual hours. You can check this in '
          + 'Configuration > Integrations.',
      },
    ],
  },
  {
    id: 'my-workplan',
    title: 'Use My Workplan',
    description: 'See the hours you are forecast for across every project, and how busy each month is.',
    category: 'Planning & Reporting',
    iconClass: 'bi bi-calendar-check',
    keywords: [
      'workplan', 'my workplan', 'schedule', 'my hours', 'assigned hours', 'capacity', 'utilization', 'availability',
      'time off', 'pto', 'vacation', 'close dates', 'holidays', 'reserved hours', 'overbooked', 'employee',
    ],
    video: {
      key: 'my-workplan.mp4',
    },
    action: { label: 'Open My Workplan', route: '/my-workplan' },
    sections: [
      {
        heading: 'What it shows',
        body:
          'My Workplan brings together every activity a person is forecast on, across all projects, month by month. '
          + 'Use it to check what you are booked for and whether any month is overloaded. The hours are read-only here. '
          + 'They come from each activity\'s forecast.',
      },
      {
        step: 1,
        heading: 'Pick a person and months',
        steps: [
          'Click My Workplan in the sidebar.',
          'Choose yourself, or anyone else, in Employee. Your choice is remembered next time.',
          'Use the arrows to move to earlier or later months, and Current to jump back to this month.',
        ],
      },
      {
        step: 2,
        heading: 'Read the activities',
        steps: [
          'Each row is an activity, grouped under its Task Name, with its Assumptions beside it.',
          'The month columns show the hours forecast for that person.',
          'Click a task name to open that discipline, where the activity\'s hours can be changed.',
        ],
      },
      {
        step: 3,
        heading: 'Check capacity and utilization',
        body: 'The Capacity & Utilization rows under the activities work out how much time is really available each month.',
        steps: [
          'Total Hours In Month: working days times 8 hours.',
          'Close Dates: company close days in that month, taken off. Hover a value to see the dates.',
          'Total Available Hours In Month: what is left after close dates.',
          'Time Off and Reserved hours: taken off next, leaving the hours free for project work.',
          'Total Scheduled Hours: all forecast hours for the month.',
          'Utilization: scheduled hours as a share of that free time. It turns red at 100% or more.',
        ],
        tip: 'A red month means the person is booked for more than they can work. Move hours to another month or another person in the activity\'s forecast.',
      },
      {
        step: 4,
        heading: 'Upcoming time off',
        steps: [
          'Upcoming Time Off Days lists booked time off by month, with the days and hours for each.',
          'Click the add button to book more. Depending on how time off is set up, it opens the Time Off Manager or EmployeeConnect.',
        ],
      },
    ],
  },
  {
    id: 'use-charts',
    title: 'Read the charts',
    description: 'Track forecast against actuals, budget burndown, and each person\'s hours and utilization.',
    category: 'Planning & Reporting',
    iconClass: 'bi bi-bar-chart-line',
    keywords: [
      'charts', 'chart', 'graph', 'report', 'burndown', 'budget', 'remaining budget', 'invoiced', 'revenue',
      'actuals', 'forecast', 'utilization', 'hours by user', 'milestones', 'export', 'download', 'png', 'csv',
      'xlsx', 'pursuits', 'interval', 'quarter', 'date range',
    ],
    video: {
      key: 'use-charts.mp4',
    },
    action: { label: 'Open charts', route: '/charts' },
    sections: [
      {
        heading: 'Open the charts',
        steps: [
          'Click Charts in the sidebar to see all projects together.',
          'Or click Charts on a project or task order page to open it already filtered to that project or task order.',
        ],
      },
      {
        step: 1,
        heading: 'Choose what to show',
        steps: [
          'Project: pick one project, or leave it on All projects.',
          'Task order: once a project is picked, narrow to one task order or keep All task orders.',
          'Scope: with All projects selected, tick Include pursuits to add pursuits to the totals.',
          'Interval: group the data by Month, Quarter, or Year.',
          'Date range: choose This quarter, This year, Last year, Project duration, or Custom range, or pick exact Dates.',
        ],
      },
      {
        step: 2,
        heading: 'Forecast vs actuals',
        body: 'The first chart compares what was planned with what happened.',
        steps: [
          'Hours shows Forecasted Hours against Actual Hours.',
          'Dollars shows the same as cost: Forecasted Dollars against Actual Dollars.',
          'Actuals only appear from the first month that has actual hours.',
        ],
      },
      {
        step: 3,
        heading: 'Budget burndown',
        body: 'Shown when a single project is selected. It tracks how much of the budget is left over time.',
        steps: [
          'Budget Target is the project or task order budget, drawn as a flat line.',
          'Invoiced Amount is the running total invoiced so far.',
          'Budget Remaining is the budget less what has been invoiced, up to the latest invoice.',
          'Budget Remaining (Projected), the dashed line, carries on from there using the forecast.',
          'Projected Revenue is the running total of forecasted dollars.',
        ],
        tip: 'If the projected line drops below zero before the project ends, the forecast is over budget.',
      },
      {
        step: 4,
        heading: 'People',
        steps: [
          'Hours by User shows each person\'s hours in each period.',
          'Click % Util to switch to Utilization by User, which shows each person\'s hours as a share of their available time. The target is 100%, and available time already allows for time off and reserved hours.',
          'Use Filter people to show only some people. Select all and Deselect all reset the list.',
        ],
      },
      {
        heading: 'Milestones and downloads',
        steps: [
          'Tick Show milestones on a chart to mark the project\'s milestones on it.',
          'Use the download button on any chart to save it as a PNG image, or its data as CSV or XLSX.',
        ],
      },
      {
        heading: 'Metrics',
        body:
          'With a single project selected, the Metrics panel at the bottom shows the Total budget, Remaining budget, '
          + 'Total hours, and Project duration.',
      },
    ],
  },
  {
    id: 'staffing-outlook',
    title: 'Staffing outlook',
    description: 'See where a task order is heading against its budget, person by person and month by month.',
    category: 'Planning & Reporting',
    iconClass: 'bi bi-people',
    keywords: [
      'staffing', 'outlook', 'estimate at completion', 'eac', 'projected total', 'budget', 'burn', 'spend',
      'hours to add', 'blended rate', 'rates', 'by person', 'export', 'under budget', 'over budget',
    ],
    video: {
      key: 'staffing-outlook.mp4',
    },
    sections: [
      {
        heading: 'Open the staffing outlook',
        steps: [
          'Open a task order.',
          'Click Staffing Outlook, next to Gantt, Actuals, Invoices, and Charts.',
        ],
      },
      {
        heading: 'How months are counted',
        body:
          'Months that are over count the hours actually logged. Months still to come count the forecast. '
          + 'The current month is in progress, so it counts whichever is higher so far. '
          + 'Hours are priced at each person\'s rate for that month, and ODCs are added on top.',
      },
      {
        step: 1,
        heading: 'Read the summary',
        steps: [
          'Budget is the task order\'s budget, or the total of its discipline budgets if the task order has none.',
          'Projected total is where the task order will finish: what has been spent so far plus what is still to come.',
          'Left in budget, or Over budget, is the difference between the two.',
          'Hours to add, or Hours to cut, is how many hours a month the team would need to add or remove, over the months the task order has left, to land exactly on the budget. It uses the team\'s blended rate.',
        ],
        tip: 'Warnings above the chart flag hours forecast after the task order ends, and hours priced at $0 because nobody had a rate in effect that month.',
      },
      {
        step: 2,
        heading: 'Read the chart',
        steps: [
          'The solid line is spend so far. It turns dashed where the forecast takes over.',
          'The dashed horizontal line is the budget.',
          'Today and Task order ends mark the current month and the end date.',
          'Hover a month to see that month\'s spend, the running total, and the share of the budget used.',
        ],
      },
      {
        step: 3,
        heading: 'Use the By person table',
        steps: [
          'Each row is a person and each column a month. Grey months are actuals, the teal month is in progress, and the rest is forecast.',
          'Switch between Hours and Dollars at the top right. Dollars adds a row for ODCs and a Cumulative % of budget row.',
          'Click any column header to sort by it. Click again to reverse the order.',
          'Rate shows every rate in effect over the period, with the months each one covered. No rate in red means hours priced at $0.',
          'Share of $ is each person\'s part of the total.',
          'Hover a past or current month to compare the forecast with the actual hours.',
        ],
        tip: 'The table shows only the months inside the task order\'s dates. Hours outside it still count in the summary and the chart.',
      },
      {
        heading: 'Export and plan',
        steps: [
          'Click Export for an Excel file with an Hours sheet and a Dollars sheet, in the same order as the table.',
          'Click Scenarios to try out staffing changes against this outlook. See the Staffing scenarios tutorial.',
        ],
      },
    ],
  },
  {
    id: 'staffing-scenarios',
    title: 'Staffing scenarios',
    description: 'Try out who works how many hours, see how it lands against availability and budget, and get changes to hand to the leads.',
    category: 'Planning & Reporting',
    iconClass: 'bi bi-sliders',
    keywords: [
      'scenario', 'what if', 'what-if', 'plan', 'staffing plan', 'availability', 'capacity', 'spend target', 'budget target',
      'hours to hit target', 'instructions', 'discipline leads', 'forecast changes', 'insert current forecast',
    ],
    video: {
      key: 'staffing-scenarios.mp4',
    },
    sections: [
      {
        heading: 'What a scenario is',
        body:
          'A scenario is a what-if for a task order: how many hours each person gives it each month over a period you choose. '
          + 'It never changes the forecast. The discipline leads own the forecast, so a scenario ends with a list of changes '
          + 'for them to make.',
      },
      {
        step: 1,
        heading: 'Create a scenario',
        steps: [
          'Open the Staffing Outlook for a task order and click Scenarios.',
          'Click New scenario.',
          'Give it a Name, then pick the First month and Last month. It defaults to this month through the end of the task order, and can cover up to 36 months.',
          'Add Notes about what you are trying out, if you like, then click Create scenario.',
        ],
      },
      {
        step: 2,
        heading: 'Enter hours',
        steps: [
          'A new scenario starts at 0 hours. Click Insert current forecast to load everyone\'s forecast for the period and adjust from there.',
          'Type each person\'s hours for each month. Under each cell, fcst is their current forecast and free is the time they have left after their other work.',
          'A cell turns red when it asks for more than the person has free that month. Hover it to see their capacity, time off, other work, and rate.',
          'Use Add person to bring in someone who is not on the task order yet. People you add can be removed with the x by their name.',
          'Click Clear hours to start again from 0.',
        ],
      },
      {
        step: 3,
        heading: 'Read the numbers',
        steps: [
          'Hours available is the time everyone has for this task order over the period: their capacity, less time off and everything they are forecast for elsewhere. The bars show it month by month.',
          'In this scenario is the hours you have entered and what they cost, including ODCs.',
          'Spend target for the period defaults to an even share of the budget that is left. Type your own target, or click Use default to go back.',
          'Hours to hit target is how many more hours the scenario needs to reach the target, at the blended rate. It turns red when the team does not have that many hours free.',
          'The line below the tiles shows where the whole task order would finish if the scenario happened.',
        ],
        tip: 'Availability is worked out one month at a time. Time off in November is never made up by spare time in December, and being over in one month does not use up room in another.',
      },
      {
        step: 4,
        heading: 'Hand changes to the leads',
        body:
          'How to get there compares the scenario with the current forecast and lists what each discipline lead would need to change, '
          + 'for example "Kelton needs 20 hours reduced from Quality Management over 3 months".',
        steps: [
          'Group the list By discipline, to send each lead their changes, or By person.',
          'Each line shows the month-by-month changes underneath.',
          'Click Copy to copy the whole list as text for an email or a message.',
        ],
        tip: 'Reductions come out of the disciplines a person is on that month, in proportion. Added hours follow the same mix, or the person\'s usual mix on the task order if they have nothing forecast that month. Someone with no discipline yet is listed for you to place.',
      },
      {
        step: 5,
        heading: 'Save and manage scenarios',
        steps: [
          'Click Save to keep your changes, or Discard to undo them. Unsaved changes shows while there is something to save.',
          'Pick another scenario from the menu at the top. Save or discard first; the menu is locked while there are unsaved changes.',
          'Click Edit details to rename the scenario, change its period, or edit its notes. Hours in months outside a new period are removed.',
          'Click Delete to remove a scenario for everyone. The forecast is not affected.',
        ],
      },
    ],
  },
  {
    id: 'wbs-manager',
    title: 'Build WBS templates',
    description: 'Save standard lists of activities as templates to reuse when setting up disciplines.',
    category: 'Planning & Reporting',
    iconClass: 'bi bi-diagram-3',
    keywords: [
      'wbs', 'wbs manager', 'work breakdown structure', 'template', 'templates', 'activities', 'items',
      'clone template', 'reorder', 'assumptions', 'standard scope',
    ],
    video: {
      key: 'wbs-manager.mp4',
    },
    action: { label: 'Open WBS Manager', route: '/wbs-manager' },
    sections: [
      {
        heading: 'What templates are for',
        body:
          'A WBS template is a reusable list of activities, each with optional assumptions. When you set up a discipline, '
          + 'you can build it from a template instead of adding every activity by hand. Each item in the template becomes '
          + 'an activity in the discipline.',
      },
      {
        step: 1,
        heading: 'Create a template',
        steps: [
          'Click WBS Manager in the sidebar.',
          'Click New template, enter a Name and optional Notes, and click Create.',
          'The template appears under Templates. Click it to open its items.',
        ],
      },
      {
        step: 2,
        heading: 'Add and edit items',
        steps: [
          'With the template selected, click Add item.',
          'Enter a Title and optional Assumptions, then click Add.',
          'Use the pencil on an item to edit it, or the red x to delete it.',
        ],
        tip: 'Write assumptions that hold for most projects. They are copied into each activity, where they can be tailored.',
      },
      {
        step: 3,
        heading: 'Put items in order',
        steps: [
          'Drag an item by its handle to move it.',
          'Click Save order when you are done. The order is kept when the template is used.',
        ],
      },
      {
        step: 4,
        heading: 'Manage templates',
        steps: [
          'Edit renames the template or changes its notes.',
          'Clone copies the template and all its items under a new name, which is quicker than starting a variation from scratch.',
          'Delete removes the template and all its items.',
        ],
      },
      {
        heading: 'Use a template',
        steps: [
          'On a task order, click Add from WBS to create a new discipline from a template, choosing which items to include.',
          'On a discipline with no activities yet, click Import from WBS to fill it from a template.',
        ],
        tip: 'Disciplines keep their own copy of the activities, so changing or deleting a template later does not affect them.',
      },
    ],
  },
  {
    id: 'manage-users',
    title: 'Add and edit users',
    description: 'Add people to ProPlan, update their details and reserved hours, and remove them.',
    category: 'Configuration',
    iconClass: 'bi bi-people',
    keywords: [
      'users', 'user', 'add user', 'new user', 'add employee', 'employee', 'edit user', 'delete user', 'remove user',
      'email', 'reserved hours', 'fte', 'utilization', 'aad', 'oid', 'sign in', 'no rates',
    ],
    video: {
      key: 'manage-users.mp4',
    },
    action: { label: 'Open users', route: '/configuration/users' },
    sections: [
      {
        heading: 'Where users live',
        body:
          'Go to Configuration > User Management. Each card under Users shows a person\'s name, email, and reserved hours. '
          + 'A No rates tag means the person has no rate on any project yet, so their hours will not have a cost.',
      },
      {
        step: 1,
        heading: 'Add a user',
        steps: [
          'Under Users, click Add.',
          'Enter the person\'s Name and Email.',
          'Leave AAD OID blank unless you have been given it. It is filled in automatically when the person signs in.',
          'Click Create.',
        ],
        tip: 'Spell the name exactly as it appears on the person\'s Microsoft account. When they first sign in, ProPlan links them to the user with the matching name.',
      },
      {
        step: 2,
        heading: 'Edit a user',
        steps: [
          'Click the person\'s card to open Edit User.',
          'Update the Name or Email.',
          'Set Reserved Hours (%) for time the person spends on non-project work.',
          'Click Save.',
        ],
        tip: 'Reserved hours lower the time available for project work. At 20%, a person has 80% of their working hours left, and utilization on the hours forecast is measured against that.',
      },
      {
        step: 3,
        heading: 'Set their rates',
        body:
          'A new user needs a rate on each project they work on. From Edit User, click Edit Rates to open their page, '
          + 'then add a rate for each project. See the Manage rates and escalations tutorial for details.',
      },
      {
        step: 4,
        heading: 'Delete a user',
        steps: [
          'Click the person\'s card, then click Delete and confirm.',
          'If the person is still a PIC or leads work on a project, the delete is blocked. Reassign that work first, then try again.',
        ],
      },
      {
        heading: 'Adding people while creating a project',
        body:
          'In the People step of the New Project wizard, you can type a name that is not in the list to create a user on '
          + 'the spot. Add their email in User Management afterwards.',
      },
    ],
  },
  {
    id: 'manage-rates',
    title: 'Manage rates',
    description: 'How project rates and effective dates work, and how to add, edit, and escalate them.',
    category: 'Configuration',
    iconClass: 'bi bi-currency-dollar',
    keywords: [
      'rates', 'rate', 'billing', 'hourly rate', 'project rate', 'effective date', 'escalate', 'escalation',
      'raise', 'increase', 'uplift', 'cost', 'historical rates', 'future rates', 'rate change',
    ],
    video: {
      key: 'manage-rates.mp4',
    },
    action: { label: 'Open rates', route: '/configuration/users' },
    sections: [
      {
        heading: 'How rates work',
        body:
          'A rate belongs to one person on one project. The same person can have a different rate on each project, '
          + 'and a project can have several rates for the same person over time. Each rate starts on its effective date '
          + 'and applies until the next one starts.',
        steps: [
          'Forecast and actual costs are hours multiplied by the rate for that month.',
          'A month uses the latest rate whose effective date is on or before the 1st of that month. A rate that starts mid-month applies from the following month.',
          'Hours in months before a person\'s first rate on a project cost $0, so make sure the first rate starts on or before the project start date.',
        ],
        tip: 'Removing someone from a project also deletes their rates and forecast hours on that project.',
      },
      {
        heading: 'Where rates are set',
        steps: [
          'When you create a project, the Rates step sets each person\'s first rate, starting on the project start date.',
          'Configuration > User Management > Rates lists rates for every person and project.',
          'Click a person\'s name to open their page, where Rates (per project) shows only their rates.',
        ],
      },
      {
        step: 1,
        heading: 'View rates',
        steps: [
          'Go to Configuration > User Management and scroll to Rates.',
          'Use the Project filter to show one project.',
          'By default only each person\'s current rate is shown. Click Show Historical/Future to see past rates and ones that have not started yet.',
        ],
      },
      {
        step: 2,
        heading: 'Add a rate',
        body: 'Add a new rate when someone\'s rate changes. Keep the old one so past months still cost what they did.',
        steps: [
          'In Rates, click Add Rate.',
          'Choose the Project and User, set the Effective Date, and enter the Rate.',
          'Click Save Rate. If that person already has a rate on the same date for that project, it is replaced.',
        ],
      },
      {
        step: 3,
        heading: 'Edit or delete a rate',
        steps: [
          'Click a rate row to open that person\'s page.',
          'Under Rates (per project), click the edit button on the rate to change the amount or its effective date.',
          'Use Delete in the same dialog to remove a rate entered by mistake.',
        ],
        tip: 'Editing or deleting a past rate changes the cost of months that already happened. To reflect a raise, add a new rate instead.',
      },
      {
        step: 4,
        heading: 'Escalate rates',
        body:
          'An escalation raises several people\'s rates on a project by the same percentage, for example an annual increase.',
        steps: [
          'In Rates, click Escalate Rates.',
          'Choose the Project and the Users to escalate.',
          'Set the Effective Date the increase starts, and enter the Escalation Percentage.',
          'Click Apply Escalation. Each person gets a new rate equal to their current rate plus the percentage, rounded to the cent.',
        ],
        tip: 'Escalation starts from each person\'s current rate, the latest one in effect today. Everyone selected needs a current rate first, and their earlier rates are left as they are.',
      },
    ],
  },
];

export const findHelpArticle = (id) => helpArticles.find((a) => a.id === id) || null;

export default helpArticles;
